import React, { useState, useEffect } from 'react';
import { FilterState } from '../types';

/**
 * FilterPanel Component
 * 
 * Provides filter controls for job list including site, press, status, and date range.
 * 
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5
 */

interface FilterPanelProps {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  hidePressFilter?: boolean;
}

type DateRangePreset = 'last1h' | 'last24h' | 'last7d' | 'last30d' | 'custom';

export const FilterPanel: React.FC<FilterPanelProps> = ({ filters, onChange, hidePressFilter = false }) => {
  const [localFilters, setLocalFilters] = useState<FilterState>(filters);
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('last1h');
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [showOldDateWarning, setShowOldDateWarning] = useState(false);

  // Press options (merged site+press)
  const pressOptions = ['All', 'CLE1', 'DFW1', 'HTD1', 'LAS1', 'MKE1', 'MKE2', 'ORL1'];

  // Site options for digital tab
  const siteOptions = ['All', 'CLE', 'DFW', 'HTD', 'LAS', 'MKE', 'ORL'];
  
  // Status options (ordered by workflow progression: received -> processing -> skipped/failed -> completed)
  const statusOptions = ['All', 'received', 'processing', 'paused', 'reprocessing', 'skipped', 'failed', 'expired', 'completed'];

  /**
   * Initialize date range on mount to match the default preset ('last1h').
   * Without this, the first API call has no startDate/endDate and returns all jobs.
   */
  useEffect(() => {
    const dateRange = calculateDateRange('last1h');
    const initialFilters = { ...filters, ...dateRange, dateRangePreset: 'last1h' as const };
    setLocalFilters(initialFilters);
    onChange(initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Check if date range includes dates older than 30 days
   * Requirement 9.2: Warn when date range is older than 30 days
   */
  const isDateRangeOlderThan30Days = (startDate?: string): boolean => {
    if (!startDate) return false;
    
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const startDateObj = new Date(startDate);
    return startDateObj < thirtyDaysAgo;
  };

  /**
   * Update warning state when filters change
   */
  useEffect(() => {
    setShowOldDateWarning(isDateRangeOlderThan30Days(localFilters.startDate));
  }, [localFilters.startDate]);

  /**
   * Calculate date range based on preset
   */
  const calculateDateRange = (preset: DateRangePreset): { startDate?: string; endDate?: string } => {
    const now = new Date();
    
    switch (preset) {
      case 'last1h': {
        const start = new Date(now.getTime() - 60 * 60 * 1000);
        return { startDate: start.toISOString(), endDate: now.toISOString() };
      }
      case 'last24h': {
        const start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        return { startDate: start.toISOString(), endDate: now.toISOString() };
      }
      case 'last7d': {
        const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return { startDate: start.toISOString(), endDate: now.toISOString() };
      }
      case 'last30d': {
        const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        return { startDate: start.toISOString(), endDate: now.toISOString() };
      }
      case 'custom':
        return {}; // User will set custom dates (YYYY-MM-DD from date picker)
      default:
        return {};
    }
  };

  /**
   * Handle filter change
   */
  const handleFilterChange = (key: 'press' | 'site' | 'status' | 'startDate' | 'endDate' | 'excludePress', value: string) => {
    const newFilters = { ...localFilters };
    
    if (value === 'All' || value === '') {
      delete newFilters[key];
    } else {
      newFilters[key] = value;
    }
    
    setLocalFilters(newFilters);
    onChange(newFilters);
  };

  /**
   * Handle date range preset change
   */
  const handleDateRangePresetChange = (preset: DateRangePreset) => {
    setDateRangePreset(preset);
    
    if (preset === 'custom') {
      setShowCustomDateRange(true);
      const newFilters = { ...localFilters, dateRangePreset: 'custom' as const };
      setLocalFilters(newFilters);
      onChange(newFilters);
    } else {
      setShowCustomDateRange(false);
      const dateRange = calculateDateRange(preset);
      const newFilters = { ...localFilters, ...dateRange, dateRangePreset: preset };
      setLocalFilters(newFilters);
      onChange(newFilters);
    }
  };

  /**
   * Convert a UTC ISO string to the value format required by datetime-local inputs
   * (YYYY-MM-DDTHH:MM:SS in local time, no timezone suffix).
   */
  const toDatetimeLocalValue = (iso?: string): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const offset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - offset).toISOString().slice(0, 19);
  };

  /**
   * Handle custom date change — converts the datetime-local value (local time)
   * to a UTC ISO string for the backend.
   */
  const handleCustomDateChange = (key: 'startDate' | 'endDate', value: string) => {
    const iso = value ? new Date(value).toISOString() : '';
    const newFilters = { ...localFilters, [key]: iso };
    setLocalFilters(newFilters);
    onChange(newFilters);
  };

  /**
   * Clear all filters
   */
  const handleClearFilters = () => {
    setLocalFilters({});
    setDateRangePreset('last1h');
    setShowCustomDateRange(false);
    onChange({});
  };

  /**
   * Count active filters (excluding date range which is always present)
   */
  const getActiveFilterCount = (): number => {
    let count = 0;
    if (localFilters.press) count++;
    if (localFilters.site) count++;
    if (localFilters.status) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
        {activeFilterCount > 0 && (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-blue-600 rounded-full">
              {activeFilterCount}
            </span>
            <button
              onClick={handleClearFilters}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Press filter - hidden when Digital tab is active */}
        {!hidePressFilter && (
          <div>
            <label htmlFor="press-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Press
            </label>
            <select
              id="press-filter"
              value={localFilters.press || 'All'}
              onChange={(e) => handleFilterChange('press', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {pressOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Site filter - shown when Digital tab is active */}
        {hidePressFilter && (
          <div>
            <label htmlFor="site-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Site
            </label>
            <select
              id="site-filter"
              value={localFilters.site || 'All'}
              onChange={(e) => handleFilterChange('site', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {siteOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Status filter */}
        <div>
          <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            id="status-filter"
            value={localFilters.status || 'All'}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {statusOptions.map((option) => (
              <option key={option} value={option}>
                {option === 'All' ? 'All' : option.charAt(0).toUpperCase() + option.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Date range preset */}
        <div>
          <label htmlFor="date-range-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Date Range
          </label>
          <select
            id="date-range-filter"
            value={dateRangePreset}
            onChange={(e) => handleDateRangePresetChange(e.target.value as DateRangePreset)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="last1h">Last 1 hour</option>
            <option value="last24h">Last 24 hours</option>
            <option value="last7d">Last 7 days</option>
            <option value="last30d">Last 30 days</option>
            <option value="custom">Custom</option>
          </select>
        </div>
      </div>

      {/* Custom date range inputs */}
      {showCustomDateRange && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200">
          <div>
            <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-1">
              Start
            </label>
            <input
              type="datetime-local"
              id="start-date"
              step="1"
              value={toDatetimeLocalValue(localFilters.startDate)}
              onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-1">
              End
            </label>
            <input
              type="datetime-local"
              id="end-date"
              step="1"
              value={toDatetimeLocalValue(localFilters.endDate)}
              onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      )}

      {/* Old Date Range Warning - Requirement 9.2 */}
      {showOldDateWarning && (
        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-sm text-yellow-800">
                <span className="font-semibold">Warning:</span> The selected date range includes dates older than 30 days. Data may be incomplete due to automatic record deletion.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;
